'use client';

import { useMemo } from 'react';

import { useSearchParams } from 'next/navigation';

import {
  flattenPortfolioAttribution,
  resolvePortfolioAttribution,
} from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import { useUtmParams } from '@/hooks/use-utm-params';
import { Analytics } from '@/lib/analytics';
import {
  type AccountCreationMethod,
  AUTH_EVENTS,
  type AuthEventBaseProperties,
  type AuthFlow,
  type AuthFunnelStep,
  type AuthStrategy,
  type SignUpChoiceClickedProperties,
} from '@/lib/analytics/auth-events';
import { getAnonymousCorrelationId } from '@/lib/analytics/get-anonymous-correlation-id';
import { pushGa4UserId } from '@/lib/analytics/push-ga4-user-id';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import type { FeatureFlagVariant } from '@/lib/feature-flags/types';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';

import { useSignUpQ1Variant } from './use-sign-up-q1-variant';

type AuthEventProperties = AuthEventBaseProperties & Record<string, unknown>;

type AuthReturnScreen =
  | 'start'
  | 'start_email_form'
  | 'email_verification'
  | 'verifications'
  | 'choose_strategy'
  | 'forgot_password'
  | 'reset_password';

interface IdentifyAuthUserParams {
  accountCreationMethod?: AccountCreationMethod;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  newsletter?: boolean;
  userId?: string;
}

function buildBaseProperties(
  variant: FeatureFlagVariant | undefined,
  utm: {
    campaign: string;
    content: string;
    medium: string;
    source: string;
    referrer: string;
    partner: string;
  },
  allowlistedParams: string,
  anonymousId: string,
  attributionProps: ReturnType<typeof flattenPortfolioAttribution>,
  overrides?: Partial<AuthEventProperties>,
): AuthEventProperties {
  const params = allowlistedParams ? Object.fromEntries(new URLSearchParams(allowlistedParams).entries()) : undefined;

  return {
    ...(variant !== undefined && { ab_test_sign_up_2026_q1: variant }),
    ...{ anonymous_id: anonymousId ?? '' },
    ...(utm.campaign && { utm_campaign: utm.campaign }),
    ...(utm.content && { utm_content: utm.content }),
    ...(utm.medium && { utm_medium: utm.medium }),
    ...(utm.source && { utm_source: utm.source }),
    ...(utm.referrer && { utm_referrer: utm.referrer }),
    ...(utm.partner && { partner: utm.partner }),
    ...attributionProps,
    ...(params && Object.keys(params).length > 0 && { search_params: params }),
    ...overrides,
  };
}

export function useAuthEvents() {
  const searchParams = useSearchParams();
  const { track } = useAnalytics();
  const { variant } = useSignUpQ1Variant();
  const { campaign, content, medium, source, referrer, partner, portfolioCode, portfolioUrl, clickIdFallbacks } =
    useUtmParams();
  const allowlistedParams = getAllowlistedSearchParams(searchParams, clickIdFallbacks);
  const portfolioAttribution = useMemo(
    () =>
      resolvePortfolioAttribution({
        portfolioCode,
        portfolioUrl,
        utmContent: content,
        referrer: null,
      }),
    [content, portfolioCode, portfolioUrl],
  );
  const attributionProps = useMemo(() => flattenPortfolioAttribution(portfolioAttribution), [portfolioAttribution]);

  const baseProps = (overrides?: Partial<AuthEventProperties>) =>
    buildBaseProperties(
      variant,
      { campaign, content, medium, source, referrer, partner },
      allowlistedParams,
      getAnonymousCorrelationId() ?? '',
      attributionProps,
      overrides,
    );

  const trackAuthEvent = (event: string, properties?: AuthEventProperties, callback?: () => void) => {
    track(event, properties, callback);
  };

  const trackAuthScreenViewed = (
    flow: AuthFlow,
    currentScreen: string,
    step?: AuthFunnelStep,
    extra?: Partial<AuthEventProperties>,
  ) => {
    trackAuthEvent(AUTH_EVENTS.AUTH_SCREEN_VIEWED, baseProps({ flow, step, current_screen: currentScreen, ...extra }));
  };

  const identifyAuthUser = ({
    userId,
    email,
    firstName,
    lastName,
    newsletter,
    accountCreationMethod,
  }: IdentifyAuthUserParams) => {
    if (userId) {
      pushGa4UserId(userId);
    }

    Analytics.identify(
      userId,
      baseProps({
        ...(email && { email }),
        first_name: firstName ?? '',
        last_name: lastName ?? '',
        ...(newsletter !== undefined && { newsletter }),
        ...(accountCreationMethod && { account_creation_method: accountCreationMethod }),
      }),
    );
  };

  const trackAuthError = (
    flow: AuthFlow,
    step: AuthFunnelStep,
    errorCode?: string,
    properties?: AuthEventProperties,
  ) => {
    trackAuthEvent(
      AUTH_EVENTS.AUTH_ERROR,
      baseProps({
        flow,
        step,
        result: 'error',
        ...(errorCode && { error_code: errorCode }),
        ...properties,
      }),
    );
  };

  const trackSignInStrategySelected = (strategy: AuthStrategy) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_STRATEGY_SELECTED,
      baseProps({ flow: 'sign_in', step: 'choose_strategy', strategy, method: 'email' }),
    );
  };

  const trackSignInSessionSelected = (choice: 'existing_session' | 'fallback', properties?: AuthEventProperties) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_SESSION_SELECTED,
      baseProps({ flow: 'sign_in', step: 'choose_session', choice, ...properties }),
    );
  };

  const trackSignUpSessionSelected = (choice: 'existing_session' | 'fallback', properties?: AuthEventProperties) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_UP_SESSION_SELECTED,
      baseProps({ flow: 'sign_up', step: 'choose_session', choice, ...properties }),
    );
  };

  const trackSignInForgotPasswordOpened = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_FORGOT_PASSWORD_OPENED,
      baseProps({ flow: 'sign_in', step: 'verifications', method: 'email' }),
    );
  };

  const trackSignInResetCodeRequested = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_RESET_CODE_REQUESTED,
      baseProps({ flow: 'sign_in', step: 'forgot_password', method: 'email' }),
    );
  };

  const trackSignInResetCodeSubmitted = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_RESET_CODE_SUBMITTED,
      baseProps({ flow: 'sign_in', step: 'reset_password', method: 'email' }),
    );
  };

  const trackSignInResetCodeResent = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_RESET_CODE_RESENT,
      baseProps({ flow: 'sign_in', step: 'reset_password', method: 'email' }),
    );
  };

  const trackSignInNewPasswordSubmitted = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_NEW_PASSWORD_SUBMITTED,
      baseProps({ flow: 'sign_in', step: 'reset_password', method: 'email' }),
    );
  };

  const trackSignInCodeResent = (strategy?: AuthStrategy) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_CODE_RESENT,
      baseProps({ flow: 'sign_in', step: 'verifications', method: 'email', ...(strategy && { strategy }) }),
    );
  };

  const trackSignUpChoiceClicked = (choice: SignUpChoiceClickedProperties['choice'], newsletter?: boolean) => {
    trackAuthEvent(AUTH_EVENTS.SIGN_UP_CHOICE_CLICKED, {
      ...baseProps({ newsletter, flow: 'sign_up', step: 'start' }),
      choice,
    });
  };

  const trackSignUpEmailReturnClicked = (currentScreen: AuthReturnScreen, redirectedScreen: AuthReturnScreen) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_UP_EMAIL_RETURN_CLICKED,
      baseProps({
        flow: 'sign_up',
        method: 'email',
        current_screen: currentScreen,
        redirected_screen: redirectedScreen,
      }),
    );
  };

  const trackSignUpStarted = (accountCreationMethod: AccountCreationMethod, newsletter?: boolean) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_UP_STARTED,
      baseProps({
        flow: 'sign_up',
        step: 'start',
        newsletter,
        account_creation_method: accountCreationMethod,
      }),
    );
  };

  const trackSignUpEmailCodeSent = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_UP_EMAIL_CODE_SENT,
      baseProps({ flow: 'sign_up', step: 'email_verification', method: 'email', account_creation_method: 'email' }),
    );
  };

  const trackSignUpEmailVerified = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_UP_EMAIL_VERIFIED,
      baseProps({
        flow: 'sign_up',
        step: 'email_verification',
        method: 'email',
        result: 'success',
        account_creation_method: 'email',
      }),
    );
  };

  const trackSignUpEmailCodeResent = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_UP_EMAIL_CODE_RESENT,
      baseProps({ flow: 'sign_up', step: 'email_verification', method: 'email', account_creation_method: 'email' }),
    );
  };

  const trackSignUpWebsiteCallbackArrived = (
    accountCreationMethod: AccountCreationMethod | undefined,
    hasSession: boolean,
  ) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_UP_WEBSITE_CALLBACK_ARRIVED,
      baseProps({
        flow: 'sign_up',
        step: 'website_callback',
        ...(accountCreationMethod && { account_creation_method: accountCreationMethod }),
        has_session: hasSession,
      }),
    );
  };

  const trackSsoSignUpCompleted = () => {
    trackAuthEvent(
      AUTH_EVENTS.SSO_SIGN_UP_COMPLETED,
      baseProps({ flow: 'sign_up', step: 'sso_callback', method: 'google', account_creation_method: 'google' }),
    );
  };

  const trackSsoSignInCompleted = () => {
    trackAuthEvent(
      AUTH_EVENTS.SSO_SIGN_IN_COMPLETED,
      baseProps({ flow: 'sign_in', step: 'sso_callback', method: 'google', account_creation_method: 'google' }),
    );
  };

  const trackSsoTransferUsed = (direction: 'sign_up_to_sign_in' | 'sign_in_to_sign_up') => {
    trackAuthEvent(
      AUTH_EVENTS.SSO_TRANSFER_USED,
      baseProps({
        flow: direction === 'sign_up_to_sign_in' ? 'sign_up' : 'sign_in',
        step: 'sso_callback',
        sso_transfer_direction: direction,
      }),
    );
  };

  const trackSsoExistingSessionReused = () => {
    trackAuthEvent(AUTH_EVENTS.SSO_EXISTING_SESSION_REUSED, baseProps({ step: 'sso_callback' }));
  };

  const trackSsoFallbackToSignIn = () => {
    trackAuthEvent(AUTH_EVENTS.SSO_FALLBACK_TO_SIGN_IN, baseProps({ flow: 'sign_in', step: 'sso_callback' }));
  };

  const trackSsoFallbackToSignUp = () => {
    trackAuthEvent(AUTH_EVENTS.SSO_FALLBACK_TO_SIGN_UP, baseProps({ flow: 'sign_up', step: 'sso_callback' }));
  };

  const trackSignInStarted = (accountCreationMethod: AccountCreationMethod) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_STARTED,
      baseProps({ flow: 'sign_in', step: 'start', account_creation_method: accountCreationMethod }),
    );
  };

  const trackSignInEmailReturnClicked = (currentScreen: AuthReturnScreen, redirectedScreen: AuthReturnScreen) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_EMAIL_RETURN_CLICKED,
      baseProps({
        flow: 'sign_in',
        method: 'email',
        current_screen: currentScreen,
        redirected_screen: redirectedScreen,
      }),
    );
  };

  const trackSignInMfaStarted = (strategy: AuthStrategy) => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_MFA_STARTED,
      baseProps({ flow: 'sign_in', step: 'verifications', method: 'email', strategy }),
    );
  };

  const trackSignInMfaCompleted = (strategy: AuthStrategy = 'email_code') => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_MFA_COMPLETED,
      baseProps({ flow: 'sign_in', step: 'verifications', method: 'email', strategy }),
    );
  };

  const trackSignInMfaBackupCodeSelected = () => {
    trackAuthEvent(
      AUTH_EVENTS.SIGN_IN_MFA_BACKUP_CODE_SELECTED,
      baseProps({ flow: 'sign_in', step: 'verifications', method: 'email' }),
    );
  };

  const trackSignInCompleted = () => {
    trackAuthEvent(AUTH_EVENTS.SIGN_IN_COMPLETED, baseProps({ flow: 'sign_in', result: 'success' }));
  };

  const trackAuthUnauthenticatedRedirected = (redirectUrl?: string) => {
    trackAuthEvent(
      AUTH_EVENTS.AUTH_UNAUTHENTICATED_REDIRECTED,
      baseProps({ flow: 'sign_in', step: 'start', ...(redirectUrl && { redirect_url: redirectUrl }) }),
    );
  };

  return {
    trackAuthEvent,
    trackAuthScreenViewed,
    trackSignInSessionSelected,
    trackSignUpSessionSelected,
    trackSignInForgotPasswordOpened,
    trackSignInResetCodeRequested,
    trackSignInResetCodeSubmitted,
    trackSignInResetCodeResent,
    trackSignInNewPasswordSubmitted,
    trackSignInCodeResent,
    trackSignUpChoiceClicked,
    trackSignUpEmailReturnClicked,
    trackSignUpStarted,
    trackSignUpEmailCodeSent,
    trackSignUpEmailVerified,
    trackSignUpEmailCodeResent,
    trackSignUpWebsiteCallbackArrived,
    trackSsoSignUpCompleted,
    trackSsoSignInCompleted,
    trackSsoTransferUsed,
    trackSsoExistingSessionReused,
    trackSsoFallbackToSignIn,
    trackSsoFallbackToSignUp,
    trackSignInStarted,
    trackSignInEmailReturnClicked,
    trackSignInMfaStarted,
    trackSignInMfaCompleted,
    trackSignInMfaBackupCodeSelected,
    trackSignInCompleted,
    trackSignInStrategySelected,
    trackAuthUnauthenticatedRedirected,
    trackAuthError,
    identifyAuthUser,
    variant,
    baseProps,
  };
}
